import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRbac } from '../contexts/RbacContext';
import { queryKeyFactory } from '../services/core/queryKeys';
import {
  CALC_METHODOLOGY_RESOURCE,
  calcMethodologyService,
  isCalcMethodologyDependentQuery,
  snapshotMethodologyLabel,
  type CalcMethodology,
  type CalcMethodologyLogEntry,
  type CalcMethodologySnapshotInfo,
  type CalcMethodologyState,
} from '../services/calcMethodology/calcMethodologyService';

/**
 * Активная методология этапов 2–4 для года планирования.
 * `methodology` — undefined, пока не загружена.
 */
export function useCalcMethodology(year: number) {
  const query = useQuery<CalcMethodologyState>({
    queryKey: queryKeyFactory.calcMethodology.state(year),
    queryFn: () => calcMethodologyService.getState(year),
    // Смену у других пользователей приносит WS (entityType 'calc-methodology').
    staleTime: 5 * 60 * 1000,
  });

  return {
    methodology: query.data?.methodology,
    state: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Видят методологию в шапке роли с любым доступом к ресурсу; «Нет доступа» — не видят. */
export function useCanViewCalcMethodology(): boolean {
  const { hasPermission } = useRbac();
  return hasPermission(CALC_METHODOLOGY_RESOURCE, 'read');
}

/** Подпись методологии утверждённой версии; ролям, которым методология не видна, — пустая. */
export function useSnapshotMethodologyLabel(): (info: CalcMethodologySnapshotInfo) => string {
  const canView = useCanViewCalcMethodology();
  return useCallback((info) => (canView ? snapshotMethodologyLabel(info) : ''), [canView]);
}

/** Переключать могут роли с «Записью»/«Полным доступом» на ресурс (manage включает write). */
export function useCanChangeCalcMethodology(): boolean {
  const { hasPermission } = useRbac();
  return hasPermission(CALC_METHODOLOGY_RESOURCE, 'write');
}

/** Журнал переключений года — для «Чтения» на ресурс. */
export function useCalcMethodologyLog(year: number, enabled: boolean, limit = 10) {
  const { hasPermission } = useRbac();
  const canRead = hasPermission(CALC_METHODOLOGY_RESOURCE, 'read');
  const query = useQuery<CalcMethodologyLogEntry[]>({
    queryKey: [...queryKeyFactory.calcMethodology.log(year), limit],
    queryFn: () => calcMethodologyService.getLog(year, limit),
    enabled: enabled && canRead,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError, canRead };
}

export function useSetCalcMethodology() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, methodology }: { year: number; methodology: CalcMethodology }) =>
      calcMethodologyService.setMethodology(year, methodology),
    onSuccess: (change) => {
      // Сразу показываем новое значение; автора подтянет повторный запрос.
      queryClient.setQueryData<CalcMethodologyState>(queryKeyFactory.calcMethodology.state(change.year), (prev) =>
        prev && { ...prev, methodology: change.current, updatedAt: change.changedAt });
      void queryClient.invalidateQueries({ queryKey: queryKeyFactory.calcMethodology.all });
    },
  });
}

/**
 * Перечитывает данные этапов 2–4 после смены методологии года — своей или чужой
 * (через WS). Монтируется один раз (в шапке). Первая загрузка значения и смена
 * года планирования переключением не считаются.
 */
export function useRefetchOnCalcMethodologyChange(year: number, methodology: CalcMethodology | undefined) {
  const queryClient = useQueryClient();
  const previousRef = useRef<{ year: number; methodology: CalcMethodology } | undefined>(undefined);

  useEffect(() => {
    if (methodology === undefined) return;
    const previous = previousRef.current;
    previousRef.current = { year, methodology };
    if (!previous || previous.year !== year || previous.methodology === methodology) return;

    void queryClient.invalidateQueries({
      predicate: (query) => isCalcMethodologyDependentQuery(query.queryKey),
    });
  }, [year, methodology, queryClient]);
}
